import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DownloadCloud, HelpCircle, Search, UserMinus } from 'lucide-react';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { UploadDropzone } from '@components/UploadDropzone';
import { CampoFecha } from '@components/CampoFecha';
import { RearmarGrupoModal } from '@components/RearmarGrupoModal';
import {
  confirmarBajaBinary,
  getBajaJob,
  getUltimasBajas,
  descargarPlantillaBajas,
  previewBajaBinary,
  type OrigenBaja,
  type PreviewBajaBin,
  type PreviewBajaBinItem,
} from '@lib/services/bajas.service';
import {
  MOTIVOS,
  motivoLabel,
  TB_EMPRESA,
  TB_FALLECIMIENTO,
  VIGENCIA_EMPRESA,
  CLASIF_CHIP,
  alertable,
  chipDeFila,
  esEditable,
  filaConfirmable,
  vigenciaEfectiva,
  mensajeAlertaDe,
  conRearmado,
  necesitaCorreccion,
  requiereRearmado,
  tipoAlertaDe,
  toDDMMYYYY,
  toISO,
  type FilaRev,
} from '@lib/bajas-revision';
import { AyudaBajasModal } from '@components/AyudaBajasModal';
import { useVistoUnaVez } from '@lib/hooks/useVistoUnaVez';
import { useAuthStore } from '@lib/store/auth.store';

function mensajeError(e: unknown): string {
  const resp = (e as { response?: { data?: { message?: string } } })?.response;
  return resp?.data?.message ?? 'Ocurrió un error.';
}

/**
 * Sección Bajas. Arriba, el flujo de 4 pasos para dar de baja INTEGRANTES en
 * Binary (feature bajas-automaticas). Debajo, siempre visible, las últimas bajas
 * hechas con búsqueda. Gateada por `view:bajas`.
 */
export function BajasPage() {
  const { user } = useAuthStore();
  // La ayuda se abre SOLA la primera vez que este usuario entra a la sección, y
  // después queda a mano en el signo de pregunta. Abrirla siempre sería ruido;
  // no abrirla nunca dejaría la documentación escondida detrás de un ícono que
  // nadie mira.
  const { visto, marcarVisto } = useVistoUnaVez('ayuda-bajas', user?.id);
  const [ayudaAbierta, setAyudaAbierta] = useState(!visto);

  const cerrarAyuda = () => {
    setAyudaAbierta(false);
    marcarVisto();
  };

  return (
    <section>
      <header className="pageHeader">
        <div className="rowBetween" style={{ alignItems: 'flex-start', gap: 12 }}>
          <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UserMinus size={22} style={{ color: '#dc2626' }} /> Bajas
          </h1>
          <button
            type="button"
            className="btn btn-ghost btn--sm"
            onClick={() => setAyudaAbierta(true)}
            title="Cómo funcionan las bajas"
            aria-label="Abrir la ayuda de la sección Bajas"
            style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}
          >
            <HelpCircle size={18} />
            <span>Ayuda</span>
          </button>
        </div>
        <p className="text-element-mid">
          Dar de baja integrantes y titulares en Binary a partir de un archivo. Se cruza cada
          persona contra Binary en vivo, se revisa y recién ahí se confirma.
        </p>
      </header>

      {ayudaAbierta && <AyudaBajasModal onClose={cerrarAyuda} />}

      <FlujoBaja />
      <UltimasBajas />
    </section>
  );
}

// ===========================================================================
// Flujo de baja (4 pasos)
// ===========================================================================

type Paso = 'cargar' | 'revisar' | 'confirmar' | 'resultado';

/** Explicación de cómo se resolvió la persona en Binary (identificador usado). */
const RESUELTO_LABEL: Record<NonNullable<PreviewBajaBinItem['resueltoPor']>, string> = {
  cuil: 'CUIL',
  afiId: 'afi_id',
  webId: 'web_id',
};

function FlujoBaja() {
  const [paso, setPaso] = useState<Paso>('cargar');
  const [preview, setPreview] = useState<PreviewBajaBin | null>(null);
  const [filas, setFilas] = useState<FilaRev[]>([]);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [jobId, setJobId] = useState('');

  const subir = useMutation({
    mutationFn: (file: File) => previewBajaBinary(file),
    onSuccess: (data) => {
      setPreview(data);
      setFilas(
        data.items.map((item) => ({
          item,
          tb: item.tbCodigo ?? '',
          vig: toISO(item.fechaVigencia),
          fall: toISO(item.fechaFallecimiento),
          sel: false,
        })),
      );
      setPaso('revisar');
    },
  });

  const confirmar = useMutation({
    mutationFn: () => {
      const items = filas.filter(filaConfirmable).map((f) => ({
        asociadoId: f.item.asociadoId as string,
        tbCodigo: f.tb,
        // Para 37020 la vigencia sale del deceso (1° del mes siguiente); el
        // server la recalcula igual, acá va la misma para que el resumen
        // muestre lo que se manda.
        fechaVigencia: toDDMMYYYY(vigenciaEfectiva(f)),
        ...(f.tb === TB_FALLECIMIENTO ? { fechaFallecimiento: toDDMMYYYY(f.fall) } : {}),
      }));
      const bloqueados = filas
        // Con el rearmado planificado deja de ser un bloqueo: se resuelve en
        // este mismo lote.
        .filter((f) => f.item.clasif === 'titular' && !f.rearmado)
        .map((f) => ({
          documento: f.item.dni,
          nombre: f.item.nombre,
          legajo: f.item.legajo,
          motivo: f.item.detalle ?? 'Es titular',
        }));
      // Van a "Alertas Bajas" dos grupos:
      //  1. Los casos que NO se automatizan (fallecimiento con grupo,
      //     fallecimiento retroactivo/manual, plan sin determinar).
      //  2. Las filas con datos incompletos que el operador NO corrigió en la
      //     revisión. Antes se quedaban en la pantalla y se perdían al cerrarla:
      //     ni se daban de baja ni quedaban registradas en ningún lado.
      const alertas = filas.filter(alertable).map((f) => ({
        asociadoId: f.item.asociadoId,
        documento: f.item.dni,
        nombre: f.item.nombre,
        legajo: f.item.legajo,
        cuil: f.item.cuil,
        afiId: f.item.afiId,
        tipo: tipoAlertaDe(f)!,
        mensaje: mensajeAlertaDe(f),
        tbCodigo: f.tb || f.item.tbCodigo,
        fechaVigencia: toDDMMYYYY(vigenciaEfectiva(f)) || f.item.fechaVigencia,
        fechaFallecimiento: toDDMMYYYY(f.fall) || f.item.fechaFallecimiento,
      }));
      /*
       * Los rearmados van en el MISMO lote. No se ejecutan al armarlos en la
       * pantalla: el operador ve la configuración de cada grupo nuevo y confirma
       * todo junto, que es lo que hace con el resto de las filas.
       */
      const rearmados = filas.filter(conRearmado).map((f) => ({
        documento: f.item.dni,
        grupos: f.rearmado!.grupos,
        dadosDeBaja: f.rearmado!.dadosDeBaja,
        motivo: f.rearmado!.tb,
        fechaVigencia: toDDMMYYYY(vigenciaEfectiva(f.rearmado!)),
        ...(f.rearmado!.tb === TB_FALLECIMIENTO
          ? { fechaFallecimiento: toDDMMYYYY(f.rearmado!.fall) }
          : {}),
      }));
      return confirmarBajaBinary({ items, bloqueados, alertas, rearmados, nombreArchivo });
    },
    onSuccess: (r) => {
      setJobId(r.jobId);
      setPaso('resultado');
    },
  });

  function reset() {
    setPreview(null);
    setFilas([]);
    setJobId('');
    subir.reset();
    confirmar.reset();
    setPaso('cargar');
  }

  return (
    <div style={cardStyle}>
      {/* El botón vive en el header de la card, no dentro del dropzone, y solo
          mientras se está cargando el archivo: en los pasos siguientes ya no
          tiene sentido ofrecer la plantilla. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <RailPasos paso={paso} />
        {paso === 'cargar' && <BotonPlantilla />}
      </div>

      {paso === 'cargar' && (
        <PasoCargar
          onFile={(file) => {
            setNombreArchivo(file.name);
            subir.mutate(file);
          }}
          cargando={subir.isPending}
          error={subir.isError ? mensajeError(subir.error) : ''}
        />
      )}

      {paso === 'revisar' && preview && (
        <PasoRevisar
          preview={preview}
          filas={filas}
          setFilas={setFilas}
          onVolver={reset}
          onContinuar={() => setPaso('confirmar')}
        />
      )}

      {paso === 'confirmar' && (
        <PasoConfirmar
          filas={filas}
          onVolver={() => setPaso('revisar')}
          onConfirmar={() => confirmar.mutate()}
          confirmando={confirmar.isPending}
          error={confirmar.isError ? mensajeError(confirmar.error) : ''}
        />
      )}

      {paso === 'resultado' && jobId && (
        <PasoResultado jobId={jobId} alertas={filas.filter(alertable).length} onNueva={reset} />
      )}
    </div>
  );
}

function BotonPlantilla() {
  const [bajando, setBajando] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-ghost"
      disabled={bajando}
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        // `btn-ghost` es transparente y sobre la card blanca casi no se ve.
        // Un gris muy suave + borde alcanza para despegarlo del fondo sin
        // competir con el botón primario del paso.
        background: 'var(--color-surface-accent, #f3f6fb)',
        border: '1px solid var(--color-outline, #d8dee9)',
      }}
      onClick={() => {
        setBajando(true);
        descargarPlantillaBajas()
          .catch(() => window.alert('No se pudo descargar la plantilla. Probá de nuevo.'))
          .finally(() => setBajando(false));
      }}
    >
      <DownloadCloud size={15} />
      {bajando ? 'Descargando…' : 'Descargar plantilla'}
    </button>
  );
}

function RailPasos({ paso }: { paso: Paso }) {
  const pasos: Array<[Paso, string]> = [
    ['cargar', 'Cargar'],
    ['revisar', 'Revisar'],
    ['confirmar', 'Confirmar'],
    ['resultado', 'Resultado'],
  ];
  const idx = pasos.findIndex(([p]) => p === paso);
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
      {pasos.map(([p, label], i) => (
        <div
          key={p}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 13,
            fontWeight: i === idx ? 700 : 500,
            color: i <= idx ? 'var(--color-primary-darker)' : 'var(--color-element-mid)',
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              background: i <= idx ? 'var(--color-primary)' : 'var(--color-surface, #eef)',
              color: i <= idx ? '#fff' : 'var(--color-element-mid)',
            }}
          >
            {i + 1}
          </span>
          {label}
          {i < pasos.length - 1 && <span style={{ opacity: 0.4, marginLeft: 4 }}>→</span>}
        </div>
      ))}
    </div>
  );
}

// ---- Paso 1: cargar ----
function PasoCargar({
  onFile,
  cargando,
  error,
}: {
  onFile: (file: File) => void;
  cargando: boolean;
  error: string;
}) {
  return (
    <>
      <UploadDropzone
        onFile={onFile}
        disabled={cargando}
        allowed={['.xlsx', '.xls', '.xlsm', '.csv']}
        hint="Formatos: .xlsx · .csv · columnas: DNI (oblig.), MOTIVO, FECHA_VIGENCIA, FECHA_FALLECIMIENTO, LEGAJO"
      />
      {cargando && (
        <p className="text-element-mid" style={{ marginTop: 12 }}>
          Procesando y cruzando contra Binary…
        </p>
      )}
      {error && (
        <p className="status status-error" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}
    </>
  );
}

// ---- Paso 2: revisar ----
function PasoRevisar({
  preview,
  filas,
  setFilas,
  onVolver,
  onContinuar,
}: {
  preview: PreviewBajaBin;
  filas: FilaRev[];
  setFilas: (f: FilaRev[]) => void;
  onVolver: () => void;
  onContinuar: () => void;
}) {
  const [bulkMotivo, setBulkMotivo] = useState('36010');
  /** Índice de la fila cuyo grupo se está rearmando, o null. De a una. */
  const [rearmando, setRearmando] = useState<number | null>(null);
  const seleccionados = filas.filter((f) => f.sel && esEditable(f.item.clasif));
  const listas = filas.filter(filaConfirmable);
  // Los desgloses por tipo de falta (sin motivo / sin fecha de fallecimiento)
  // los reemplazó el cubo `corregir`: ahora el lote no avanza hasta que no
  // quede ninguna, y cada fila dice en la columna Estado qué le falta.

  // Cubos EXCLUYENTES: cada fila cae en uno solo, así los contadores suman el
  // total y el operador sabe cuántas quedan por tocar.
  const cubos = useMemo(() => {
    let listos = 0;
    let corregir = 0;
    let alertas = 0;
    let rearmadas = 0;
    let fuera = 0;
    for (const f of filas) {
      // Con el reparto decidido la fila ya no es un pendiente: viaja en el
      // lote como rearmado y no va a Alertas.
      if (f.rearmado) rearmadas++;
      else if (filaConfirmable(f)) listos++;
      else if (alertable(f)) alertas++;
      else if (necesitaCorreccion(f)) corregir++;
      else fuera++;
    }
    return { listos, corregir, alertas, rearmadas, fuera };
  }, [filas]);

  function toggle(i: number) {
    setFilas(filas.map((f, j) => (j === i ? { ...f, sel: !f.sel } : f)));
  }
  function setMotivo(i: number, tb: string) {
    setFilas(filas.map((f, j) => (j === i ? { ...f, tb } : f)));
  }
  function setCampo(i: number, campo: 'vig' | 'fall', valor: string) {
    setFilas(filas.map((f, j) => (j === i ? { ...f, [campo]: valor } : f)));
  }
  function aplicarBulk() {
    setFilas(filas.map((f) => (f.sel && esEditable(f.item.clasif) ? { ...f, tb: bulkMotivo } : f)));
  }

  return (
    <>
      {/* Los contadores se calculan sobre las MISMAS filas de la tabla y en
          cubos excluyentes, así suman siempre el total. Antes salían del
          preview y se pisaban entre sí ("sin motivo" también contaba dentro de
          "con error"), y el total no cerraba: 6+1+2+0+0+4 = 13 sobre 12 filas. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Contador label="Se dan de baja" valor={cubos.listos} color="#166534" />
        <Contador label="A corregir acá" valor={cubos.corregir} color="#b45309" />
        <Contador label="A Alertas Bajas" valor={cubos.alertas} color="#6d28d9" />
        {cubos.rearmadas > 0 && (
          <Contador label="Grupos a rearmar" valor={cubos.rearmadas} color="#166534" />
        )}
        <Contador label="No se procesan" valor={cubos.fuera} color="#6b7280" />
        <Contador label="Total de filas" valor={filas.length} />
      </div>

      {seleccionados.length > 0 && (
        <div style={bulkBarStyle}>
          <strong style={{ color: 'var(--color-primary-darker)' }}>
            {seleccionados.length} seleccionado(s)
          </strong>
          <span style={{ color: 'var(--color-element-mid)', fontSize: 13 }}>Cambiar motivo a</span>
          <select
            value={bulkMotivo}
            onChange={(e) => setBulkMotivo(e.target.value)}
            style={{ ...inputBoxStyle, minWidth: 220 }}
          >
            {MOTIVOS.map((m) => (
              <option key={m.codigo} value={m.codigo}>
                {m.label} ({m.codigo})
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-primary" onClick={aplicarBulk}>
            Aplicar a los {seleccionados.length}
          </button>
        </div>
      )}

      {/* La columna que decide la acción (ESTADO) va TERCERA y no última: con
          9 columnas la tabla se iba a 1326px sobre 1064 disponibles y "Se dará
          de baja" quedaba detrás de un scroll horizontal que nadie descubre.
          Los identificadores (legajo, afi_id) se apilan bajo la persona: son
          para diagnóstico, no para decidir. */}
      <div className="tableWrap" style={{ maxHeight: 640, overflow: 'auto' }}>
        <table className="dataTable dataTable--stickyHead dataTable--headContrast">
          <thead>
            <tr>
              <th style={{ width: 34 }}></th>
              <th style={{ minWidth: 190 }}>Integrante</th>
              <th style={{ minWidth: 190 }}>Estado</th>
              <th>Motivo</th>
              <th>Vigencia</th>
              <th>F. fallecimiento</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => {
              const chip = chipDeFila(f);
              const editable = esEditable(f.item.clasif);
              return (
                <tr key={`${f.item.dni}-${i}`}>
                  <td>
                    {editable ? (
                      <input type="checkbox" checked={f.sel} onChange={() => toggle(i)} />
                    ) : null}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{f.item.nombre || '—'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--color-element-mid)' }}>
                      DNI {f.item.dni || '—'}
                      {f.item.legajo && ` · ${f.item.legajo}`}
                    </div>
                    {f.item.afiId && (
                      <div
                        title={
                          f.item.resueltoPor
                            ? `Resuelto en Binary por ${RESUELTO_LABEL[f.item.resueltoPor]}`
                            : undefined
                        }
                        style={{
                          fontSize: 10.5,
                          fontFamily: 'monospace',
                          color: 'var(--color-element-mid)',
                        }}
                      >
                        afi {f.item.afiId}
                        {f.item.resueltoPor && ` · ${RESUELTO_LABEL[f.item.resueltoPor]}`}
                      </div>
                    )}
                  </td>
                  <td>
                    {/* El chip ya sale del estado actual de la fila (color y
                        texto), así que no hace falta ni el reemplazo del texto
                        ni la línea de abajo repitiendo qué falta. */}
                    <span style={{ ...chipStyle, color: chip.color, background: chip.bg }}>
                      {chip.label}
                    </span>
                    {!editable && !f.rearmado && f.item.detalle && (
                      <div
                        style={{ fontSize: 11, color: 'var(--color-element-mid)', marginTop: 3 }}
                      >
                        {f.item.detalle}
                      </div>
                    )}
                    {/* La acción va acá, pegada al motivo por el que la fila no
                        se puede dar de baja sola: es la respuesta a eso, no una
                        columna aparte. */}
                    {requiereRearmado(f) && (
                      <div style={{ marginTop: 6 }}>
                        {/* Con el reparto decidido se muestra CÓMO queda: es lo
                            que el operador va a confirmar, y sin verlo tendría
                            que volver a abrir el modal para acordarse. */}
                        {f.rearmado && (
                          <div
                            style={{
                              fontSize: 11.5,
                              color: 'var(--color-element-mid)',
                              marginBottom: 4,
                              lineHeight: 1.4,
                            }}
                          >
                            {f.rearmado.resumen}
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => setRearmando(i)}
                          style={{ padding: '3px 10px', fontSize: 12 }}
                        >
                          {f.rearmado ? 'Cambiar el reparto' : 'Rearmar grupo →'}
                        </button>
                        {f.rearmado && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() =>
                              setFilas(
                                filas.map((x, j) =>
                                  j === i ? { ...x, rearmado: undefined } : x,
                                ),
                              )
                            }
                            style={{ padding: '3px 10px', fontSize: 12, marginLeft: 6 }}
                          >
                            Quitar
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {editable ? (
                      <select
                        value={f.tb}
                        onChange={(e) => setMotivo(i, e.target.value)}
                        style={selectFilaStyle}
                      >
                        <option value="">— falta —</option>
                        {MOTIVOS.map((m) => (
                          <option key={m.codigo} value={m.codigo}>
                            {m.label} ({m.codigo})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: 'var(--color-element-mid)' }}>—</span>
                    )}
                  </td>
                  <td>
                    {(() => {
                      if (!editable) {
                        return <span style={{ color: 'var(--color-element-mid)' }}>—</span>;
                      }
                      // Con 36010 el server fuerza la vigencia al 1° del mes
                      // próximo. Antes se mostraba el input deshabilitado con la
                      // fecha DEL ARCHIVO y la aclaración solo en un tooltip: el
                      // operador leía una fecha que no era la que se iba a usar.
                      // Ahora se muestra la fecha real y por qué es esa.
                      if (f.tb === TB_EMPRESA) {
                        return (
                          <>
                            <div style={{ fontWeight: 600 }}>{VIGENCIA_EMPRESA}</div>
                            <div style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>
                              Baja por empresa: siempre el 1° del mes próximo
                            </div>
                          </>
                        );
                      }
                      // Fallecimiento: la vigencia no se carga, se deriva del
                      // deceso (1° del mes siguiente). Regla de Medicus del
                      // 02/10/2026: las vigencias son siempre 1° de mes.
                      if (f.tb === TB_FALLECIMIENTO) {
                        const vig = vigenciaEfectiva(f);
                        return (
                          <>
                            <div style={{ fontWeight: 600 }}>
                              {vig ? toDDMMYYYY(vig) : '—'}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--color-element-mid)' }}>
                              Fallecimiento: el 1° del mes siguiente al deceso
                            </div>
                          </>
                        );
                      }
                      return (
                        <CampoFecha value={f.vig} onChange={(iso) => setCampo(i, 'vig', iso)} />
                      );
                    })()}
                  </td>
                  <td>
                    {editable && f.tb === TB_FALLECIMIENTO ? (
                      <CampoFecha value={f.fall} onChange={(iso) => setCampo(i, 'fall', iso)} />
                    ) : (
                      <span style={{ color: 'var(--color-element-mid)' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 16,
          flexWrap: 'wrap',
        }}
      >
        <div className="text-element-mid" style={{ fontSize: 13 }}>
          {/* Lo primero que se lee es si el lote puede avanzar o no. Antes el
              texto arrancaba anunciando "se enviarán N cápitas" aunque el botón
              estuviera bloqueado, y explicaba cómo corregir el fallecimiento
              rehaciendo el archivo — cuando la fecha se carga en la propia fila. */}
          {cubos.corregir > 0 ? (
            <div style={{ color: '#b45309', fontWeight: 600 }}>
              Hay {cubos.corregir} fila(s) para corregir: completales el dato que pide la columna
              Estado. No se puede continuar hasta resolverlas.
            </div>
          ) : (
            <div>
              Se enviarán a Binary{' '}
              <strong style={{ color: 'var(--color-element)' }}>{listas.length}</strong> cápita(s).
            </div>
          )}
          <div style={{ marginTop: 4 }}>
            {[
              cubos.listos > 0 && `${cubos.listos} se dan de baja`,
              cubos.alertas > 0 && `${cubos.alertas} van a Alertas Bajas`,
              cubos.fuera > 0 && `${cubos.fuera} no se procesan`,
            ]
              .filter(Boolean)
              .join(' · ')}
            {cubos.fuera > 0 && (
              <span style={{ fontSize: 12 }}>
                {' '}
                (repetidas en el archivo, ya de baja, o no encontradas en Binary)
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" className="btn btn-ghost" onClick={onVolver}>
            Volver
          </button>
          {/* Un dato mal cargado no puede seguir viaje: antes se podía
              continuar y esas filas terminaban registradas como alerta, o sea
              que el error se descubría después de confirmar el lote. Lo único
              que no frena son las repetidas del archivo, que se ignoran solas. */}
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              cubos.corregir > 0 ||
              (listas.length === 0 && preview.alertas === 0 && cubos.rearmadas === 0)
            }
            title={
              cubos.corregir > 0
                ? `Corregí las ${cubos.corregir} fila(s) marcadas en ámbar para continuar`
                : undefined
            }
            onClick={onContinuar}
          >
            Continuar →
          </button>
        </div>
      </div>

      {rearmando !== null && (
        <RearmarGrupoModal
          item={filas[rearmando].item}
          plan={filas[rearmando].rearmado}
          onClose={() => setRearmando(null)}
          onPlan={(plan) => {
            // Solo se guarda la decisión. Lo que la ejecuta es el "Confirmar"
            // del final, junto con el resto del lote.
            setFilas(filas.map((f, j) => (j === rearmando ? { ...f, rearmado: plan } : f)));
            setRearmando(null);
          }}
        />
      )}
    </>
  );
}

// ---- Paso 3: confirmar ----
function PasoConfirmar({
  filas,
  onVolver,
  onConfirmar,
  confirmando,
  error,
}: {
  filas: FilaRev[];
  onVolver: () => void;
  onConfirmar: () => void;
  confirmando: boolean;
  error: string;
}) {
  const listas = filas.filter(filaConfirmable);
  const rearmados = filas.filter(conRearmado);
  // `alertable` y no la clasificación: una fila con el reparto decidido se
  // resuelve en este lote y NO va a Alertas, aunque su clasificación siga
  // siendo una de las que iban.
  const alertasCount = filas.filter(alertable).length;
  const porMotivo = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of listas) m.set(f.tb, (m.get(f.tb) ?? 0) + 1);
    return [...m.entries()];
  }, [listas]);
  // Desglose de las que NO se dan de baja acá y quedan registradas como alerta,
  // por tipo: "2 alertas" sin decir de qué no le sirve a nadie.
  const porAlerta = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of filas.filter(alertable)) {
      const label = CLASIF_CHIP[f.item.clasif].label.replace(/\s*·\s*a Alertas$/, '');
      m.set(label, (m.get(label) ?? 0) + 1);
    }
    return [...m.entries()];
  }, [filas]);
  // "Cápitas" y no "integrantes": el lote mezcla familiares con titulares
  // (`titular_solo` y `grupo_completo` son baja del legajo entero), así que el
  // total son personas y el desglose aclara de qué tipo es cada una.
  const titulares = listas.filter(
    (f) =>
      f.item.clasif === 'titular_solo' || (f.item.clasif === 'grupo_completo' && f.item.esTitular),
  ).length;
  const integrantes = listas.length - titulares;
  return (
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <p style={{ fontSize: 17, fontWeight: 600, maxWidth: 560, margin: '0 auto' }}>
        Vas a dar de baja a <span style={{ color: '#bb2436' }}>{listas.length} cápita(s)</span> en
        Binary
        {rearmados.length > 0 && (
          <>
            {' '}
            y a rearmar{' '}
            <span style={{ color: '#bb2436' }}>
              {rearmados.length} grupo{rearmados.length === 1 ? '' : 's'}
            </span>
          </>
        )}
        . Esta acción genera solicitudes reales en el motor de afiliaciones.
      </p>
      {/* Resumen en tres bloques: a cuánta gente, por qué motivo, y qué queda
          registrado como alerta. Sin vigencias: cada fila ya lleva la suya y se
          revisó en el paso anterior; repetirlas acá agregaba una fecha suelta
          que no dice nada sobre lo que se está por confirmar. */}
      <div style={resumenCardStyle}>
        {/* Los rearmados van PRIMERO y con el detalle completo: son lo más
            difícil de deshacer del lote, y lo único donde el operador tomó
            decisiones que no están en el archivo. */}
        {rearmados.length > 0 && (
          <div style={resumenFilaStyle}>
            <div style={kkStyle}>Grupos a rearmar</div>
            <div style={{ textAlign: 'left' }}>
              {rearmados.map((f) => (
                <div key={f.item.dni} style={{ marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                    {f.item.nombre || f.item.dni}
                    {f.item.legajo && (
                      <span
                        className="text-element-mid"
                        style={{ fontWeight: 400, marginLeft: 6, fontSize: 12 }}
                      >
                        legajo {f.item.legajo}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--color-element-mid)' }}>
                    {f.rearmado?.resumen}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={resumenFilaStyle}>
          <div style={kkStyle}>Cápitas</div>
          <div>
            <span style={vvStyle}>{listas.length}</span>
            <span style={{ fontSize: 12.5, color: 'var(--color-element-mid)', marginLeft: 8 }}>
              {[
                integrantes > 0 && `${integrantes} integrante${integrantes === 1 ? '' : 's'}`,
                titulares > 0 &&
                  `${titulares} titular${titulares === 1 ? '' : 'es'} (baja de legajo)`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </div>

        <div style={resumenFilaStyle}>
          <div style={kkStyle}>Motivos</div>
          <div>
            {porMotivo.length === 0 ? (
              <span style={{ color: 'var(--color-element-mid)' }}>—</span>
            ) : (
              porMotivo.map(([c, n]) => (
                <div key={c} style={{ fontSize: 14 }}>
                  <strong>{n}</strong> · {motivoLabel(c)}
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ ...resumenFilaStyle, borderBottom: 'none' }}>
          <div style={kkStyle}>Alertas</div>
          <div>
            {alertasCount === 0 ? (
              <span style={{ color: 'var(--color-element-mid)' }}>Ninguna</span>
            ) : (
              <>
                {porAlerta.map(([label, n]) => (
                  <div key={label} style={{ fontSize: 14, color: '#6d28d9' }}>
                    <strong>{n}</strong> · {label}
                  </div>
                ))}
                <div style={{ fontSize: 12, color: 'var(--color-element-mid)', marginTop: 2 }}>
                  Se registran en Alertas Bajas. No se dan de baja acá.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <button type="button" className="btn btn-ghost" onClick={onVolver} disabled={confirmando}>
          Volver a revisar
        </button>
        <button
          type="button"
          className="btn"
          style={{ background: '#bb2436', color: '#fff' }}
          onClick={onConfirmar}
          disabled={confirmando || (listas.length === 0 && alertasCount === 0 && rearmados.length === 0)}
        >
          {confirmando
            ? 'Procesando…'
            : listas.length === 0
              ? 'Registrar alertas'
              : 'Confirmar y dar de baja'}
        </button>
      </div>
      {error && (
        <p className="status status-error" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}
    </div>
  );
}

// ---- Paso 4: resultado ----
export function PasoResultado({
  jobId,
  alertas,
  onNueva,
}: {
  jobId: string;
  /** Filas que el confirmar mandó a "Alertas Bajas" (no se dan de baja acá). */
  alertas: number;
  onNueva: () => void;
}) {
  const job = useQuery({
    queryKey: ['baja-job', jobId],
    queryFn: () => getBajaJob(jobId),
    refetchInterval: (query) => (query.state.data?.estado === 'corriendo' ? 1500 : false),
  });
  const d = job.data;

  // Al terminar el job, "Últimas bajas" (y las alertas que haya registrado el
  // lote) quedaron viejas: se refrescan solas en vez de obligar a recargar la
  // página para ver lo que se acaba de dar de baja.
  const qc = useQueryClient();
  const estadoJob = d?.estado;
  useEffect(() => {
    if (!estadoJob || estadoJob === 'corriendo') return;
    // Las dos invalidaciones van encadenadas con un `.catch` real y no con el
    // operador `void`: Sonar lo marca como smell de impacto ALTO (S3735) y con
    // umbral 0 frena el Quality Gate del MR. El catch además no se traga el
    // error: si el refresco falla queda en consola en vez de dejar la pantalla
    // mostrando datos viejos sin que nadie se entere.
    const refrescar = async () => {
      await qc.invalidateQueries({ queryKey: ['ultimas-bajas'] });
      await qc.invalidateQueries({ queryKey: ['alertas-bajas'] });
    };
    refrescar().catch((err: unknown) => {
      console.error('No se pudieron refrescar Últimas bajas y Alertas', err);
    });
  }, [estadoJob, qc]);

  const pct = d && d.total > 0 ? Math.round((d.procesados / d.total) * 100) : 0;

  return (
    <>
      {!d && <p className="text-element-mid">Cargando…</p>}
      {d && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            <strong>
              {d.estado === 'corriendo'
                ? 'Procesando bajas…'
                : d.estado === 'error'
                  ? 'El proceso falló'
                  : 'Bajas terminadas'}{' '}
              {d.procesados} de {d.total}
            </strong>
            <span className="text-element-mid">{pct}%</span>
          </div>
          <div
            style={{
              height: 9,
              background: 'var(--color-surface, #eef)',
              borderRadius: 999,
              overflow: 'hidden',
              border: '1px solid var(--color-outline)',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${pct}%`,
                background: 'var(--color-primary)',
                transition: 'width .3s',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '16px 0' }}>
            <Contador label="Baja confirmada" valor={d.exitosos} color="#166534" />
            <Contador label="Rechazadas / omitidas" valor={d.fallidos} color="#b91c1c" />
            {/* El job solo conoce lo que se INTENTÓ dar de baja; las alertas se
                registran aparte al confirmar, así que el conteo viene del lote
                y no del job. Sin esto el resultado daba a entender que las
                filas que no se procesaron se habían perdido. */}
            <Contador label="A Alertas Bajas" valor={alertas} color="#6d28d9" />
          </div>

          {alertas > 0 && (
            <p className="text-element-mid" style={{ fontSize: 13, marginTop: -6 }}>
              {alertas === 1 ? 'Se registró 1 caso' : `Se registraron ${alertas} casos`} en{' '}
              <a href="/alertas-bajas" style={{ fontWeight: 600 }}>
                Alertas Bajas
              </a>{' '}
              para resolver a mano.
            </p>
          )}

          {d.error && <p className="status status-error">{d.error}</p>}

          {d.resultados && d.resultados.length > 0 && (
            <div className="tableWrap" style={{ maxHeight: 360, overflow: 'auto' }}>
              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Integrante</th>
                    <th>Legajo</th>
                    <th>N° solicitud</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {d.resultados.map((r, i) => (
                    <tr key={`${r.asociadoId}-${i}`}>
                      <td style={{ fontWeight: 600 }}>{r.nombre || r.documento || '—'}</td>
                      <td>{r.legajo || '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.solafiId || '—'}</td>
                      <td>
                        {r.estado === 'baja_ok' ? (
                          <span style={{ ...chipStyle, color: '#166534', background: '#e2f3e8' }}>
                            Baja OK
                          </span>
                        ) : (
                          <span
                            style={{ ...chipStyle, color: '#b91c1c', background: '#f9e2e5' }}
                            title={r.error}
                          >
                            {r.estado === 'omitida' ? 'Omitida' : 'Rechazó'}
                            {r.error ? ` · ${r.error}` : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onNueva}
              disabled={d.estado === 'corriendo'}
            >
              Nueva carga
            </button>
          </div>
        </>
      )}
    </>
  );
}

// ===========================================================================
// Últimas bajas (panel de consulta, siempre visible)
// ===========================================================================
export function UltimasBajas() {
  const [q, setQ] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [origen, setOrigen] = useState<OrigenBaja>('todas');
  const query = useQuery({
    // `origen` va en la key: sin eso la lista filtrada se serviría desde la
    // caché de la sin filtrar.
    queryKey: ['ultimas-bajas', busqueda, pageSize, origen],
    queryFn: () => getUltimasBajas(1, pageSize, busqueda, origen),
  });
  const total = query.data?.total ?? 0;

  // Cambiar de filtro vuelve a la primera tanda: si no, quedarías pidiendo 50
  // filas de un conjunto que tiene 3 y la pantalla parecería vacía de más.
  const cambiarOrigen = (v: OrigenBaja) => {
    setOrigen(v);
    setPageSize(10);
  };

  return (
    <div style={{ ...cardStyle, marginTop: 22 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 14,
        }}
      >
        <h2 className="heading-4" style={{ margin: 0 }}>
          Últimas bajas
        </h2>
        <span className="text-element-mid" style={{ fontSize: 13 }}>
          {total} en total
        </span>
      </div>

      <div role="group" aria-label="Origen de la baja" style={filtroOrigenStyle}>
        <FiltroOrigen actual={origen} valor="todas" onSelect={cambiarOrigen}>
          Todas
        </FiltroOrigen>
        <FiltroOrigen actual={origen} valor="portal" onSelect={cambiarOrigen}>
          Hechas desde el portal
        </FiltroOrigen>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setBusqueda(q.trim());
          setPageSize(10);
        }}
        style={{ display: 'flex', gap: 8, marginBottom: 14, maxWidth: 520 }}
      >
        <div style={campoBusquedaStyle}>
          <Search size={16} style={{ opacity: 0.6 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="DNI, CUIL, nombre, legajo o N° de solicitud"
            style={inputInlineStyle}
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Buscar
        </button>
      </form>

      {query.isLoading && <TableSkeleton rows={4} />}
      {query.data && (
        <>
          {query.data.items.length === 0 ? (
            <p className="text-element-mid">
              {origen === 'portal'
                ? 'Todavía no hay bajas despachadas desde el portal. Las que ves en “Todas” se hicieron en Binary y acá solo quedaron espejadas.'
                : 'No hay bajas que coincidan.'}
            </p>
          ) : (
            <div className="tableWrap">
              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Integrante</th>
                    <th>DNI</th>
                    <th>Legajo</th>
                    <th>Motivo</th>
                    <th>Vigencia</th>
                    <th>N° solicitud</th>
                    <th>Ejecutada</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((b) => (
                    <tr key={b.asociadoId}>
                      <td style={{ fontWeight: 600 }}>{b.nombre || '—'}</td>
                      <td>{b.documento || '—'}</td>
                      <td>{b.legajo || '—'}</td>
                      <td style={{ color: 'var(--color-element-mid)' }}>
                        {b.tbCodigo ? motivoLabel(b.tbCodigo) : b.motivo || '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{b.fechaVigencia || '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.solafiId || '—'}</td>
                      <td
                        className="text-element-mid"
                        style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                      >
                        {b.at ? new Date(b.at).toLocaleDateString('es-AR') : '—'}
                        {b.ejecutadoPor ? ` · ${b.ejecutadoPor}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {query.data.items.length < total && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPageSize((n) => n + 10)}
              >
                Ver más
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- helpers de UI ----------
const filtroOrigenStyle: CSSProperties = {
  display: 'flex',
  gap: 6,
  marginBottom: 14,
  flexWrap: 'wrap',
};

/** Botón del filtro de origen. Aislado para no inflar `UltimasBajas`. */
function FiltroOrigen({
  actual,
  valor,
  onSelect,
  children,
}: {
  actual: OrigenBaja;
  valor: OrigenBaja;
  onSelect: (v: OrigenBaja) => void;
  children: ReactNode;
}) {
  const activo = actual === valor;
  return (
    <button
      type="button"
      className={activo ? 'btn btn-primary' : 'btn btn-ghost'}
      aria-pressed={activo}
      onClick={() => onSelect(valor)}
      style={{ padding: '6px 14px', fontSize: 13 }}
    >
      {children}
    </button>
  );
}

function Contador({ label, valor, color }: { label: string; valor: number; color?: string }) {
  return (
    <div
      style={{
        minWidth: 110,
        background: 'var(--color-surface, #f7f9fb)',
        border: '1px solid var(--color-outline)',
        borderRadius: 11,
        padding: '10px 13px',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: color ?? 'var(--color-element)' }}>
        {valor}
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>{label}</div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-outline)',
  borderRadius: 'var(--radius-md)',
  padding: '18px 18px',
  background: 'var(--color-surface, #fff)',
};
const campoBusquedaStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 12px',
  border: '1px solid var(--color-outline)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-background)',
};
const inputInlineStyle: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  padding: '10px 0',
  background: 'transparent',
  fontSize: 14,
};
const inputBoxStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--color-outline)',
  borderRadius: 'var(--radius-md)',
  fontSize: 14,
  minWidth: 200,
};
const selectFilaStyle: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--color-outline)',
  borderRadius: 8,
  fontSize: 12.5,
  maxWidth: 210,
};
const bulkBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 12,
  padding: '10px 14px',
  border: '1px solid var(--color-primary)',
  background: 'var(--color-primary-lightest, #dceef1)',
  borderRadius: 10,
};
const chipStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 700,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};
const kkStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  color: 'var(--color-element-mid)',
  fontWeight: 700,
};
const vvStyle: React.CSSProperties = { fontSize: 15, fontWeight: 700, marginTop: 3 };
/** Resumen del paso Confirmar: una tarjeta angosta, alineada a la izquierda,
 *  con una fila por concepto (cápitas / motivos / alertas). */
const resumenCardStyle: React.CSSProperties = {
  maxWidth: 520,
  margin: '18px auto',
  textAlign: 'left',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 10,
  padding: '4px 16px',
};
const resumenFilaStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '96px 1fr',
  alignItems: 'baseline',
  gap: 12,
  padding: '12px 0',
  borderBottom: '1px solid var(--color-border, #eef0f2)',
};
